import React, { FormEvent, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Logo } from '../Logo';
import { useAuth } from '../../contexts/AuthContext';

interface LoginFormProps {
  redirectTo: string;
  initialEmail?: string;
  autoFocus?: boolean;
  showBranding?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  redirectTo,
  initialEmail = '',
  autoFocus = true,
  showBranding = true,
}) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();

  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const buildRegisterLink = (nextEmail: string) => {
    const params = new URLSearchParams({ redirect: redirectTo });

    if (nextEmail) {
      params.set('email', nextEmail);
    }

    return `/register?${params.toString()}`;
  };

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!value) {
      setEmailError(t('auth.login.emailRequired'));
      return false;
    }

    if (!emailRegex.test(value)) {
      setEmailError(t('auth.login.invalidEmail'));
      return false;
    }

    setEmailError('');
    return true;
  };

  const validatePassword = (value: string): boolean => {
    if (!value) {
      setPasswordError(t('auth.login.passwordRequired'));
      return false;
    }

    if (value.length < 6) {
      setPasswordError(t('auth.login.passwordMin'));
      return false;
    }

    setPasswordError('');
    return true;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);

    try {
      await login(email, password);
      navigate(redirectTo);
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.login.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        {showBranding && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Logo size="large" />
          </Box>
        )}
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          {t('auth.login.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auth.login.subtitle')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('common.fields.email')}
          type="email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (emailError) {
              validateEmail(event.target.value);
            }
          }}
          onBlur={() => validateEmail(email)}
          error={!!emailError}
          helperText={emailError}
          margin="normal"
          required
          autoComplete="email"
          autoFocus={autoFocus}
          disabled={loading}
        />

        <TextField
          fullWidth
          label={t('common.fields.password')}
          type="password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            if (passwordError) {
              validatePassword(event.target.value);
            }
          }}
          onBlur={() => validatePassword(password)}
          error={!!passwordError}
          helperText={passwordError}
          margin="normal"
          required
          autoComplete="current-password"
          disabled={loading}
        />

        <Box sx={{ textAlign: 'right', mt: 1 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            variant="body2"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {t('auth.login.forgotPassword')}
          </Link>
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            mt: 2,
            mb: 2,
            py: 1.5,
            fontWeight: 600,
            background: 'linear-gradient(135deg, #4A7C59 0%, #355A40 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #355A40 0%, #2A4833 100%)',
            },
          }}
        >
          {loading ? t('auth.login.submitting') : t('auth.login.submit')}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('auth.login.noAccount')}{' '}
            <Link
              component={RouterLink}
              to={buildRegisterLink(email)}
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('auth.login.signUp')}
            </Link>
          </Typography>
        </Box>
      </form>
    </Box>
  );
};

export default LoginForm;